import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import AntdAppHelper from './components/AntdAppHelper';
import zhCN from 'antd/locale/zh_CN';
import Login from './pages/Login';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminStatistics from './pages/Campus/Statistics';
import CampusManagement from './pages/Admin/CampusManagement';
import AdminManagement from './pages/Admin/AdminManagement';
import LogManagement from './pages/Admin/LogManagement';
import Settings from './pages/Admin/Settings';
import CampusDashboard from './pages/Campus/Dashboard';
import CampusAccountManagement from './pages/Campus/AccountManagement';
import StudentManagement from './pages/Campus/StudentManagement';
import GradeManagement from './pages/Campus/GradeManagement';
import GradeQuery from './pages/Query';
import './App.css';

// Educational App 配色方案 - UI/UX Pro Max 推荐
// 靛蓝主色 + 活力橙强调色，传达知识、信任与年轻活力
const customTheme = {
  token: {
    // 主色 - 靛蓝 (Indigo)
    colorPrimary: '#4F46E5',
    colorPrimaryHover: '#6366F1',
    colorPrimaryActive: '#4338CA',
    
    // 辅助色 - 浅靛蓝
    colorInfo: '#818CF8',
    
    // 成功色（成绩合格）
    colorSuccess: '#10B981',
    colorSuccessBg: '#D1FAE5',
    colorSuccessBorder: '#A7F3D0',
    
    // 错误色（成绩不合格/操作失败）
    colorError: '#DC2626',
    colorErrorBg: '#FEE2E2',
    colorErrorBorder: '#FECACA',
    
    // 警告色/强调色 - 活力橙 (CTA 按钮、重要提示)
    colorWarning: '#EA580C',
    colorWarningBg: '#FFEDD5',
    
    // 背景色 - 极浅蓝
    colorBgContainer: '#FFFFFF',
    colorBgLayout: '#EEF2FF',
    colorBgElevated: '#FFFFFF',
    
    // 文字色 - 深蓝黑
    colorText: '#1E1B4B',
    colorTextSecondary: '#475569',
    colorTextTertiary: '#64748B',
    
    // 边框色 - 淡蓝灰
    colorBorder: '#C7D2FE',
    colorBorderSecondary: '#E2E8F0',
    
    // 圆角
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    
    // 阴影
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    
    // 字体
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    Button: {
      borderRadius: 8,
      controlHeight: 36,
    },
    Card: {
      borderRadius: 12,
      paddingLG: 24,
    },
    Table: {
      borderRadius: 8,
      headerBg: '#EEF2FF',
      headerColor: '#1E1B4B',
    },
    Menu: {
      borderRadius: 8,
      itemBorderRadius: 6,
    },
    Input: {
      borderRadius: 8,
      controlHeight: 40,
    },
    Select: {
      borderRadius: 8,
      controlHeight: 40,
    },
    Tag: {
      borderRadius: 6,
    },
    Badge: {
      colorError: '#DC2626',
    },
  },
};

// 受保护路由组件
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userInfo = localStorage.getItem('userInfo');
    
    if (token && userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      setIsAuthenticated(true);
      setUserRole(parsedUserInfo.role);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

// 角色权限守卫组件 - 用于页面级别的权限控制
// allowedRoles: 允许访问的角色列表
// deniedRoles: 禁止访问的角色列表
const RoleGuard: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
  deniedRoles?: string[];
}> = ({ children, allowedRoles, deniedRoles }) => {
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      setUserRole(parsedUserInfo.role);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  // 检查是否在禁止角色列表中
  if (deniedRoles && userRole && deniedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // 检查是否在允许角色列表中
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={customTheme}>
      <AntdApp>
        <AntdAppHelper />
        <Router>
        <Routes>
          {/* 公开路由 */}
          <Route path="/login" element={<Login />} />
          <Route path="/query" element={<GradeQuery />} />
          
          {/* 超级管理员路由 */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminLayout userRole="super_admin" />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="campuses" element={<CampusManagement />} />
            <Route path="admins" element={<AdminManagement />} />
            <Route path="students" element={<StudentManagement />} />
            <Route path="grades" element={<GradeManagement />} />
            <Route path="statistics" element={<AdminStatistics />} />
            <Route path="logs" element={<LogManagement />} />
            <Route path="settings" element={<Settings />} />
            <Route path="" element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* 校区负责人路由 */}
          <Route
            path="/campus/*"
            element={
              <ProtectedRoute allowedRoles={['campus_admin', 'uploader']}>
                <AdminLayout userRole="campus_admin" />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<CampusDashboard />} />
            <Route path="students" element={<StudentManagement />} />
            <Route path="grades" element={<GradeManagement />} />
            {/* 只有校区负责人可以访问成绩审核页面，上传专员不能访问 */}
            <Route path="grade-review" element={
              <RoleGuard deniedRoles={['uploader']}>
                <GradeManagement />
              </RoleGuard>
            } />
            {/* 只有校区负责人可以访问成绩统计页面，上传专员不能访问 */}
            <Route path="statistics" element={
              <RoleGuard deniedRoles={['uploader']}>
                <AdminStatistics />
              </RoleGuard>
            } />
            {/* 只有校区负责人可以访问账号管理页面，上传专员不能访问 */}
            <Route path="accounts" element={
              <RoleGuard deniedRoles={['uploader']}>
                <CampusAccountManagement />
              </RoleGuard>
            } />
            <Route path="" element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* 默认路由 */}
          <Route path="/" element={<Navigate to="/query" replace />} />
          <Route path="*" element={<Navigate to="/query" replace />} />
        </Routes>
      </Router>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
